import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { inr, categoryLabel, imgUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Users, Building2, CalendarCheck, IndianRupee, ShieldCheck, Clock,
  CheckCircle2, Ban, Trash2, Car, RefreshCw, Download, Phone, Mail, Plus, Pencil, XCircle,
  FileText, ShieldAlert,
} from "lucide-react";

const StatCard = ({ icon: Icon, label, value, tone = "#FF385C" }) => (
  <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`} className="bg-white border border-[#EBEBEB] rounded-2xl p-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${tone}1A` }}>
        <Icon className="w-5 h-5" style={{ color: tone }} />
      </div>
      <div>
        <p className="text-2xl font-display font-bold leading-none">{value}</p>
        <p className="text-xs text-[#717171] mt-1">{label}</p>
      </div>
    </div>
  </div>
);

const statusBadge = (h) => {
  if (h.is_blocked) return <Badge className="bg-[#FDE8E8] text-[#C13515] border-0">Blocked</Badge>;
  if (h.status === "approved") return <Badge className="bg-[#E6F4EA] text-[#137333] border-0">Approved</Badge>;
  if (h.status === "rejected") return <Badge className="bg-[#F0F0F4] text-[#717171] border-0">Rejected</Badge>;
  if (h.status === "unpublished") return <Badge className="bg-[#F0F0F4] text-[#717171] border-0">Unpublished</Badge>;
  return <Badge className="bg-[#FEF3E0] text-[#B36B00] border-0">Pending</Badge>;
};

const verifyBadge = (h) => {
  if (h.verification_status === "pending_verification") return <Badge className="bg-[#FEF3E0] text-[#B36B00] border-0">Pending Verification</Badge>;
  if (h.verification_status === "verified") return <Badge className="bg-[#E6F4EA] text-[#137333] border-0">Verified</Badge>;
  if (h.verification_status === "expired") return <Badge className="bg-[#FDE8E8] text-[#C13515] border-0">Verification expired</Badge>;
  return null;
};

const daysLeft = (deadline) => {
  if (!deadline) return null;
  return Math.ceil((new Date(deadline) - new Date()) / 86400000);
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [users, setUsers] = useState([]);
  const [services, setServices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSvc, setEditSvc] = useState(null);
  const [svcForm, setSvcForm] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h, u, sv, b, w] = await Promise.all([
        api.get("/admin/stats"), api.get("/admin/hotels"), api.get("/admin/users"),
        api.get("/admin/services"), api.get("/admin/bookings"), api.get("/admin/waitlist"),
      ]);
      setStats(s.data); setHotels(h.data); setUsers(u.data);
      setServices(sv.data); setBookings(b.data); setWaitlist(w.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const act = async (fn, okMsg) => {
    try { await fn(); if (okMsg) toast.success(okMsg); await loadAll(); }
    catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const cancelBooking = (b) => {
    if (!window.confirm(`Cancel booking for ${b.guest_name} at ${b.hotel_name}?`)) return;
    act(() => api.post(`/bookings/${b.id}/cancel`), "Booking cancelled");
  };

  const openEditSvc = (s) => {
    setSvcForm({
      type: s.type, name: s.name, description: s.description || "", city: s.city,
      price_from: s.price_from, contact_phone: s.contact_phone || "",
      contact_whatsapp: s.contact_whatsapp || "", images: s.images || [],
    });
    setEditSvc(s);
  };

  const saveSvc = async () => {
    try {
      await api.put(`/provider/services/${editSvc.id}`, { ...svcForm, price_from: Number(svcForm.price_from) });
      toast.success("Add-on updated");
      setEditSvc(null);
      await loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Update failed"); }
  };

  const pending = hotels.filter((h) => h.status === "pending");
  const pendingVerification = hotels.filter((h) => h.verification_status === "pending_verification");

  const exportCsv = (rows, columns, filename) => {
    if (!rows.length) { toast.error("Nothing to export yet"); return; }
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const header = columns.map((c) => esc(c.label)).join(",");
    const body = rows.map((r) => columns.map((c) => esc(r[c.key])).join(",")).join("\n");
    const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    a.click(); URL.revokeObjectURL(url);
  };

  if (loading && !stats) return <div className="py-24 text-center text-[#717171]">Loading admin panel…</div>;

  return (
    <div className="bg-[#F7F7F9] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-display font-bold flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-[#FF385C]" /> Admin Control Center
            </h1>
            <p className="text-[#717171] text-sm mt-1">Manage users, properties, add-ons, approvals & bookings.</p>
          </div>
          <Button data-testid="admin-refresh-btn" variant="outline" className="rounded-xl" onClick={loadAll}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
            <StatCard icon={Users} label="Users" value={stats.users} />
            <StatCard icon={ShieldCheck} label="Admins" value={stats.admins} tone="#137333" />
            <StatCard icon={Clock} label="Pending" value={stats.pending_hotels} tone="#B36B00" />
            <StatCard icon={Building2} label="Properties" value={stats.hotels} tone="#2563EB" />
            <StatCard icon={CalendarCheck} label="Bookings" value={stats.confirmed_bookings} tone="#7C3AED" />
            <StatCard icon={IndianRupee} label="Revenue" value={inr(stats.revenue)} tone="#137333" />
          </div>
        )}

        <Tabs defaultValue="verification">
          <TabsList className="bg-white border border-[#EBEBEB] rounded-xl p-1 flex-wrap h-auto">
            <TabsTrigger value="verification" data-testid="tab-verification" className="rounded-lg">
              Verification {pendingVerification.length > 0 && <span className="ml-1 text-xs bg-[#FF385C] text-white rounded-full px-1.5">{pendingVerification.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="approvals" data-testid="tab-approvals" className="rounded-lg">
              Approvals {pending.length > 0 && <span className="ml-1 text-xs bg-[#FF385C] text-white rounded-full px-1.5">{pending.length}</span>}
            </TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-properties" className="rounded-lg">Properties</TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users" className="rounded-lg">Users</TabsTrigger>
            <TabsTrigger value="services" data-testid="tab-services" className="rounded-lg">Add-ons</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-lg">Bookings</TabsTrigger>
            <TabsTrigger value="waitlist" data-testid="tab-waitlist" className="rounded-lg">
              Waitlist {waitlist.length > 0 && <span className="ml-1 text-xs bg-[#222] text-white rounded-full px-1.5">{waitlist.length}</span>}
            </TabsTrigger>
          </TabsList>

          {/* VERIFICATION (Fast-Track) */}
          <TabsContent value="verification" className="mt-5">
            <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5">
              <h2 className="font-display font-semibold text-lg mb-1 flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-[#B36B00]" /> Properties pending verification</h2>
              <p className="text-xs text-[#717171] mb-4">These are live now. Verify within 7 days or they auto-unpublish. Review the host&apos;s ID documents below.</p>
              {pendingVerification.length === 0 ? (
                <p className="text-[#717171] py-6 text-center">🎉 Nothing to verify. All caught up!</p>
              ) : (
                <div className="space-y-3">
                  {pendingVerification.map((h) => {
                    const dl = daysLeft(h.verification_deadline);
                    return (
                      <div key={h.id} data-testid={`verify-${h.id}`} className="border border-[#EBEBEB] rounded-xl p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-semibold">{h.name} <span className="text-xs text-[#717171]">· {categoryLabel(h.category)} · {h.city}</span></p>
                            <p className="text-xs text-[#717171] mt-0.5">Legal name: <strong className="text-[#222]">{h.legal_name || "—"}</strong> · {h.room_count} room(s)</p>
                            <p className="text-xs mt-0.5">
                              {verifyBadge(h)}
                              <span className={`ml-2 ${dl != null && dl <= 2 ? "text-[#C13515] font-semibold" : "text-[#717171]"}`}>
                                {dl != null ? (dl > 0 ? `${dl} day(s) left` : "Deadline passed") : ""}
                              </span>
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button data-testid={`verify-approve-${h.id}`} className="rounded-xl bg-[#137333] hover:bg-[#0d5c28] text-white" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/verify`), "Property verified")}>
                              <CheckCircle2 className="w-4 h-4 mr-1" /> Verify
                            </Button>
                            <Button data-testid={`verify-unpublish-${h.id}`} variant="outline" className="rounded-xl text-[#C13515] border-[#F3C0B3]" onClick={() => { if (window.confirm(`Unpublish "${h.name}"? It will be removed from the public site.`)) act(() => api.post(`/admin/hotels/${h.id}/unpublish`), "Property unpublished"); }}>
                              Unpublish
                            </Button>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          {(h.id_documents || []).length === 0 ? (
                            <span className="text-xs text-[#C13515]">No ID documents uploaded</span>
                          ) : (
                            h.id_documents.map((doc, i) => (
                              <a key={i} href={imgUrl(doc)} target="_blank" rel="noopener noreferrer" data-testid={`verify-doc-${h.id}-${i}`} className="inline-flex items-center gap-1.5 text-xs border border-[#EBEBEB] rounded-lg px-2.5 py-1.5 text-[#222] hover:border-[#FF385C] transition-colors">
                                <FileText className="w-3.5 h-3.5 text-[#FF385C]" /> ID {i + 1} <Download className="w-3 h-3 text-[#717171]" />
                              </a>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>

          {/* APPROVALS */}
          <TabsContent value="approvals" className="mt-5">
            <div className="bg-white border border-[#EBEBEB] rounded-2xl p-5">
              <h2 className="font-display font-semibold text-lg mb-4">Properties awaiting approval</h2>
              {pending.length === 0 ? (
                <p className="text-[#717171] py-6 text-center">🎉 No pending properties. All caught up!</p>
              ) : (
                <div className="space-y-3">
                  {pending.map((h) => (
                    <div key={h.id} data-testid={`pending-${h.id}`} className="flex flex-wrap items-center justify-between gap-3 border border-[#EBEBEB] rounded-xl p-4">
                      <div>
                        <p className="font-semibold">{h.name} <span className="text-xs text-[#717171]">· {categoryLabel(h.category)} · {h.city}</span></p>
                        <p className="text-xs text-[#717171]">{h.room_count} room(s) · {h.min_price ? inr(h.min_price) + "/night" : "no price"}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button data-testid={`approve-${h.id}`} className="rounded-xl bg-[#137333] hover:bg-[#0d5c28] text-white" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/approve`), "Property approved & live")}>
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button data-testid={`reject-${h.id}`} variant="outline" className="rounded-xl text-[#C13515] border-[#F3C0B3]" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/status?status=rejected`), "Property rejected")}>
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* PROPERTIES */}
          <TabsContent value="properties" className="mt-5">
            <div className="flex justify-end mb-3">
              <Button data-testid="admin-list-property-btn" className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white" onClick={() => navigate("/host/properties/new")}>
                <Plus className="w-4 h-4 mr-1" /> List a property
              </Button>
            </div>
            <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#717171] text-xs">
                  <tr><th className="text-left p-3">Property</th><th className="text-left p-3">City</th><th className="text-left p-3">Rooms</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {hotels.map((h) => (
                    <tr key={h.id} data-testid={`hotel-row-${h.id}`} className="border-t border-[#EEE]">
                      <td className="p-3 font-medium">{h.name}<span className="block text-xs text-[#717171]">{categoryLabel(h.category)}</span></td>
                      <td className="p-3">{h.city}</td>
                      <td className="p-3">{h.room_count}</td>
                      <td className="p-3"><div className="flex flex-col gap-1 items-start">{statusBadge(h)}{verifyBadge(h)}</div></td>
                      <td className="p-3">
                        <div className="flex gap-1.5 justify-end flex-wrap">
                          {h.verification_status === "pending_verification" && (
                            <Button size="sm" data-testid={`p-verify-${h.id}`} className="rounded-lg bg-[#137333] hover:bg-[#0d5c28] text-white h-8" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/verify`), "Verified")}>Verify</Button>
                          )}
                          {h.status === "approved" && (
                            <Button size="sm" variant="outline" data-testid={`p-unpublish-${h.id}`} className="rounded-lg h-8 text-[#C13515] border-[#F3C0B3]" onClick={() => { if (window.confirm(`Unpublish "${h.name}"?`)) act(() => api.post(`/admin/hotels/${h.id}/unpublish`), "Unpublished"); }}>Unpublish</Button>
                          )}
                          {h.status !== "approved" && (
                            <Button size="sm" data-testid={`p-approve-${h.id}`} className="rounded-lg bg-[#137333] hover:bg-[#0d5c28] text-white h-8" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/approve`), "Approved")}>Approve</Button>
                          )}
                          <Button size="sm" variant="outline" data-testid={`p-edit-${h.id}`} className="rounded-lg h-8" onClick={() => navigate(`/host/properties/${h.id}`)}>
                            <Pencil className="w-3.5 h-3.5 mr-1" />Edit
                          </Button>
                          <Button size="sm" variant="outline" data-testid={`p-block-${h.id}`} className="rounded-lg h-8" onClick={() => act(() => api.post(`/admin/hotels/${h.id}/block`, { blocked: !h.is_blocked }), h.is_blocked ? "Unblocked" : "Blocked")}>
                            <Ban className="w-3.5 h-3.5 mr-1" />{h.is_blocked ? "Unblock" : "Block"}
                          </Button>
                          <Button size="sm" variant="ghost" data-testid={`p-delete-${h.id}`} className="rounded-lg h-8 text-[#C13515]" onClick={() => { if (window.confirm(`Delete "${h.name}" permanently?`)) act(() => api.delete(`/admin/hotels/${h.id}`), "Deleted"); }}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="mt-5">
            <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#717171] text-xs">
                  <tr><th className="text-left p-3">User</th><th className="text-left p-3">Contact</th><th className="text-left p-3">Role</th><th className="text-left p-3">Admin</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-[#EEE]">
                      <td className="p-3 font-medium">{u.name}<span className="block text-xs text-[#717171]">{u.email}</span></td>
                      <td className="p-3" data-testid={`user-phone-${u.id}`}>
                        {u.phone ? <span className="flex items-center gap-1 text-[#222]"><Phone className="w-3.5 h-3.5 text-[#717171]" />{u.phone}</span> : <span className="text-[#B0B0B0]">—</span>}
                      </td>
                      <td className="p-3">
                        <Select value={u.role} onValueChange={(v) => act(() => api.post(`/admin/users/${u.id}/role`, { role: v }), "Role updated")}>
                          <SelectTrigger data-testid={`role-select-${u.id}`} className="rounded-lg h-8 w-40"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="traveler">Traveler</SelectItem>
                            <SelectItem value="host">Host</SelectItem>
                            <SelectItem value="service_provider">Service provider</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-3">
                        <Switch data-testid={`admin-toggle-${u.id}`} checked={!!u.is_admin} onCheckedChange={(v) => act(() => api.post(`/admin/users/${u.id}/admin`, { is_admin: v }), v ? "Granted admin" : "Revoked admin")} />
                      </td>
                      <td className="p-3">{u.is_blocked ? <Badge className="bg-[#FDE8E8] text-[#C13515] border-0">Blocked</Badge> : <Badge className="bg-[#E6F4EA] text-[#137333] border-0">Active</Badge>}</td>
                      <td className="p-3 text-right">
                        <Button size="sm" variant="outline" data-testid={`user-block-${u.id}`} className="rounded-lg h-8" onClick={() => act(() => api.post(`/admin/users/${u.id}/block`, { blocked: !u.is_blocked }), u.is_blocked ? "Unblocked" : "Blocked")}>
                          {u.is_blocked ? "Unblock" : "Block"}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* SERVICES */}
          <TabsContent value="services" className="mt-5">
            <div className="flex justify-end mb-3">
              <Button data-testid="admin-list-addon-btn" className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white" onClick={() => navigate("/provider")}>
                <Plus className="w-4 h-4 mr-1" /> List an add-on
              </Button>
            </div>
            <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#717171] text-xs">
                  <tr><th className="text-left p-3">Service</th><th className="text-left p-3">Type</th><th className="text-left p-3">City</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {services.length === 0 ? (
                    <tr><td colSpan={5} className="p-6 text-center text-[#717171]">No add-on services listed.</td></tr>
                  ) : services.map((s) => (
                    <tr key={s.id} data-testid={`service-row-${s.id}`} className="border-t border-[#EEE]">
                      <td className="p-3 font-medium flex items-center gap-2"><Car className="w-4 h-4 text-[#717171]" />{s.name}</td>
                      <td className="p-3 capitalize">{s.type}</td>
                      <td className="p-3">{s.city}</td>
                      <td className="p-3">{s.is_blocked ? <Badge className="bg-[#FDE8E8] text-[#C13515] border-0">Blocked</Badge> : <Badge className="bg-[#E6F4EA] text-[#137333] border-0">Live</Badge>}</td>
                      <td className="p-3 text-right">
                        <div className="flex gap-1.5 justify-end">
                          <Button size="sm" variant="outline" data-testid={`svc-edit-${s.id}`} className="rounded-lg h-8" onClick={() => openEditSvc(s)}><Pencil className="w-3.5 h-3.5 mr-1" />Edit</Button>
                          <Button size="sm" variant="outline" data-testid={`svc-block-${s.id}`} className="rounded-lg h-8" onClick={() => act(() => api.post(`/admin/services/${s.id}/block`, { blocked: !s.is_blocked }), s.is_blocked ? "Unblocked" : "Blocked")}>{s.is_blocked ? "Unblock" : "Block"}</Button>
                          <Button size="sm" variant="ghost" data-testid={`svc-delete-${s.id}`} className="rounded-lg h-8 text-[#C13515]" onClick={() => { if (window.confirm(`Delete "${s.name}"?`)) act(() => api.delete(`/admin/services/${s.id}`), "Deleted"); }}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-5">
            <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#717171] text-xs">
                  <tr><th className="text-left p-3">Property / Service</th><th className="text-left p-3">Guest</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Status</th><th className="text-right p-3">Actions</th></tr>
                </thead>
                <tbody>
                  {bookings.slice(0, 100).map((b) => (
                    <tr key={b.id} data-testid={`booking-row-${b.id}`} className="border-t border-[#EEE]">
                      <td className="p-3 font-medium">{b.hotel_name}<span className="block text-xs text-[#717171]">{b.booking_type}</span></td>
                      <td className="p-3">{b.guest_name}</td>
                      <td className="p-3">{inr(b.total_price)}</td>
                      <td className="p-3">{b.status === "confirmed" ? <Badge className="bg-[#E6F4EA] text-[#137333] border-0">Confirmed</Badge> : b.status === "cancelled" ? <Badge className="bg-[#FDE8E8] text-[#C13515] border-0">Cancelled</Badge> : <Badge className="bg-[#FEF3E0] text-[#B36B00] border-0">{b.status}</Badge>}</td>
                      <td className="p-3 text-right">
                        {b.status !== "cancelled" && (
                          <Button size="sm" variant="outline" data-testid={`admin-cancel-booking-${b.id}`} className="rounded-lg h-8 text-[#C13515] border-[#F3C0B3]" onClick={() => cancelBooking(b)}><XCircle className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* WAITLIST */}
          <TabsContent value="waitlist" className="mt-5">
            <div className="bg-white border border-[#EBEBEB] rounded-2xl overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b border-[#EEE]">
                <div>
                  <h2 className="font-display font-semibold text-lg">Waitlist signups</h2>
                  <p className="text-xs text-[#717171]">{waitlist.length} people waiting for launch</p>
                </div>
                <Button data-testid="export-waitlist-btn" variant="outline" className="rounded-xl" onClick={() => exportCsv(waitlist, [
                  { key: "email", label: "Email" }, { key: "name", label: "Name" },
                  { key: "source", label: "Source" }, { key: "created_at", label: "Joined at" },
                ], `ixitrip-waitlist-${new Date().toISOString().slice(0, 10)}.csv`)}>
                  <Download className="w-4 h-4 mr-1" /> Export CSV
                </Button>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#717171] text-xs">
                  <tr><th className="text-left p-3">Email</th><th className="text-left p-3">Name</th><th className="text-left p-3">Source</th><th className="text-left p-3">Joined</th></tr>
                </thead>
                <tbody>
                  {waitlist.length === 0 ? (
                    <tr><td colSpan={4} className="p-6 text-center text-[#717171]">No signups yet.</td></tr>
                  ) : waitlist.map((w) => (
                    <tr key={w.id} data-testid={`waitlist-row-${w.id}`} className="border-t border-[#EEE]">
                      <td className="p-3 font-medium flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-[#717171]" />{w.email}</td>
                      <td className="p-3">{w.name || <span className="text-[#B0B0B0]">—</span>}</td>
                      <td className="p-3 capitalize">{w.source}</td>
                      <td className="p-3 text-[#717171]">{new Date(w.created_at).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!editSvc} onOpenChange={(o) => !o && setEditSvc(null)}>
        <DialogContent className="rounded-2xl" data-testid="admin-edit-svc-dialog">
          <DialogHeader><DialogTitle>Edit travel add-on</DialogTitle></DialogHeader>
          {editSvc && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label className="text-xs mb-1 block">Name</Label><Input value={svcForm.name} onChange={(e) => setSvcForm({ ...svcForm, name: e.target.value })} className="rounded-xl" /></div>
              <div><Label className="text-xs mb-1 block">Type</Label>
                <Select value={svcForm.type} onValueChange={(v) => setSvcForm({ ...svcForm, type: v })}>
                  <SelectTrigger className="rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="bike">bike</SelectItem><SelectItem value="car">car</SelectItem><SelectItem value="taxi">taxi</SelectItem><SelectItem value="yacht">yacht</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">City</Label><Input value={svcForm.city} onChange={(e) => setSvcForm({ ...svcForm, city: e.target.value })} className="rounded-xl" /></div>
              <div><Label className="text-xs mb-1 block">Starting price (₹)</Label><Input type="number" value={svcForm.price_from} onChange={(e) => setSvcForm({ ...svcForm, price_from: e.target.value })} className="rounded-xl" /></div>
              <div><Label className="text-xs mb-1 block">Phone</Label><Input value={svcForm.contact_phone} onChange={(e) => setSvcForm({ ...svcForm, contact_phone: e.target.value })} className="rounded-xl" /></div>
              <div className="col-span-2"><Label className="text-xs mb-1 block">Description</Label><Input value={svcForm.description} onChange={(e) => setSvcForm({ ...svcForm, description: e.target.value })} className="rounded-xl" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" className="rounded-xl" onClick={() => setEditSvc(null)}>Cancel</Button>
            <Button data-testid="admin-save-svc" className="rounded-xl bg-[#FF385C] hover:bg-[#D90B38] text-white" onClick={saveSvc}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
